import React, { useEffect, useRef, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Contrast } from 'lucide-react';
import useModalA11y from '../hooks/useModalA11y';
import { readPrefs, setContrastPref } from '../utils/a11yPrefs';

// Routes where the floating button is shown. The preferences themselves are
// applied globally on every page load via initA11yPrefs() in App.jsx.
const VISIBLE_ROUTES = ['/', '/home'];
// In the logged-in product the same toggles live in Profile -> Settings
// (Display Options section) instead of a floating button.

// Floating accessibility options button (WCAG G174 style switcher).
// Default palette keeps brand colors; "High contrast" swaps the known
// low-contrast tokens via [data-contrast="high"] overrides in index.css.
const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const { pathname } = useLocation();

  useModalA11y(open, () => setOpen(false), panelRef);

  useEffect(() => {
    const prefs = readPrefs();
    setHighContrast(prefs.contrastOn);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const isVisibleRoute = VISIBLE_ROUTES.some((pattern) => matchPath(pattern, pathname));
  if (!isVisibleRoute) return null;



  const toggleContrast = (next) => {
    setHighContrast(next);
    setContrastPref(next);
  };


  return (
    <div ref={containerRef} className="fixed left-5 md:left-6 z-[95] bottom-4">
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Display options"
          tabIndex={-1}
          className="outline-none absolute bottom-14 left-0 w-64 rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
        >
          <h2 className="mb-3 text-[15px] font-semibold text-gray-900">Display options</h2>
          <div className="flex flex-col gap-3">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={highContrast}
                onChange={(e) => toggleContrast(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-gray-300 accent-black"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">High contrast</span>
                <span className="block text-xs text-gray-700">
                  Darkens light text and highlights for easier reading.
                </span>
              </span>
            </label>
            <p className="text-[11px] text-gray-600 border-t border-gray-100 pt-2">
              Your choice is saved and applies on every page.
            </p>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Display options"
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-10 w-10 md:h-14 md:w-14 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-800 shadow-lg hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black"
      >
        <Contrast className="h-5 w-5 md:h-6 md:w-6" aria-hidden="true" />
      </button>
    </div>
  );
};

export default AccessibilityWidget;
